/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { inferVendorIcon } from '@/lib/model-icon'
import { useStatus } from '@/hooks/use-status'
import { getPricing } from '../api'

export function usePricingData() {
  const { status } = useStatus()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pricing'],
    queryFn: getPricing,
    staleTime: 5 * 60 * 1000,
  })

  // Ensure rates never reach zero to prevent division errors
  const priceRate = useMemo(
    () => Math.max((status?.price as number) ?? 1, 0.001),
    [status?.price]
  )
  const usdExchangeRate = useMemo(
    () => Math.max((status?.usd_exchange_rate as number) ?? priceRate, 0.001),
    [status?.usd_exchange_rate, priceRate]
  )

  const models = useMemo(() => {
    if (!data?.data || !data?.vendors) return []

    const vendorMap = new Map(data.vendors.map((v) => [v.id, v]))
    const tencentVendor = data.vendors.find((v) => v.name === '腾讯')
    const youtuVendor = data.vendors.find(
      (v) => v.name === '优图' || v.name === 'YouTu' || v.name === 'Youtu'
    )
    const miniMaxVendor = data.vendors.find((v) => v.name === 'MiniMax')

    return data.data.map((model) => {
      const vendor = model.vendor_id
        ? vendorMap.get(model.vendor_id)
        : undefined
      const inferredIcon = inferVendorIcon(model.model_name || '')
      const modelLower = (model.model_name || '').toLowerCase()
      const isYouTu =
        modelLower.startsWith('youtu-') || modelLower.startsWith('yt-')
      const isMiniMax = modelLower.startsWith('minimax-')
      if (!vendor && isYouTu) {
        const targetVendor = youtuVendor || tencentVendor
        if (targetVendor) {
          return {
            ...model,
            key: model.model_name,
            vendor_id: targetVendor.id,
            vendor_name: targetVendor.name,
            vendor_icon: inferredIcon || targetVendor.icon,
            vendor_description: targetVendor.description,
            group_ratio: data.group_ratio,
          }
        }
        return {
          ...model,
          key: model.model_name,
          vendor_name: '优图',
          vendor_icon: inferredIcon,
          group_ratio: data.group_ratio,
        }
      }
      if (!vendor && isMiniMax) {
        if (miniMaxVendor) {
          return {
            ...model,
            key: model.model_name,
            vendor_id: miniMaxVendor.id,
            vendor_name: miniMaxVendor.name,
            vendor_icon: inferredIcon || miniMaxVendor.icon,
            vendor_description: miniMaxVendor.description,
            group_ratio: data.group_ratio,
          }
        }
        return {
          ...model,
          key: model.model_name,
          vendor_name: 'MiniMax',
          vendor_icon: inferredIcon,
          group_ratio: data.group_ratio,
        }
      }
      const isHunyuan =
        modelLower.startsWith('hy-') ||
        modelLower.startsWith('hy3-') ||
        modelLower.includes('hunyuan')
      if (!vendor && isHunyuan) {
        if (tencentVendor) {
          return {
            ...model,
            key: model.model_name,
            vendor_id: tencentVendor.id,
            vendor_name: tencentVendor.name,
            vendor_icon: inferredIcon,
            vendor_description: tencentVendor.description,
            group_ratio: data.group_ratio,
          }
        }
        return {
          ...model,
          key: model.model_name,
          vendor_name: '腾讯',
          vendor_icon: inferredIcon,
          group_ratio: data.group_ratio,
        }
      }
      return {
        ...model,
        key: model.model_name,
        vendor_name: vendor?.name,
        vendor_icon: vendor?.icon || inferredIcon,
        vendor_description: vendor?.description,
        group_ratio: data.group_ratio,
      }
    })
  }, [data])

  return {
    models,
    vendors: data?.vendors ?? [],
    groupRatio: data?.group_ratio ?? {},
    usableGroup: data?.usable_group ?? {},
    endpointMap: data?.supported_endpoint ?? {},
    autoGroups: data?.auto_groups ?? [],
    isLoading,
    error,
    refetch,
    priceRate,
    usdExchangeRate,
  }
}
